import { combineReducers } from 'redux'
import { createResponsiveStateReducer } from 'redux-responsive'
import { routerReducer } from 'react-router-redux'
import StoreService from '../services/ephemeral-store'
import StringTableService from '../services/string-table'
import UtilService from '../services/util'
import OspreyApi from '../actions/osprey-api'
import AccessPolicyService from '../services/access-policy'
import { defaultMessageObject, defaultOffer } from '../services/data-organizer'
import { NOTIFICATION_TYPE } from '../components/notification'

import {
    APP_BUSY_DECREMENT,
    APP_BUSY_INCREMENT,
    AUTH_FAILURE,
    AUTH_LOGOUT,
    AUTH_REQUEST,
    AUTH_SUCCESS,
    CONTENT_RECEIVE,
    CONTENT_REQUEST,
    CURRENT_CLIENT_RESET,
    CURRENT_CLIENT_UPDATE,
    DISMISS_NOTIFICATION,
    DRAG_DROP_FILES,
    DRAG_DROP_RESET,
    DRAWER_CLOSE,
    DRAWER_OPEN,
    ERROR_NOTIFICATION,
    INFO_NOTIFICATION,
    MESSAGE_CREATE,
    OFFER_CREATE,
    PAGE_MODE_CHANGE,
    SILO_SETTINGS,
    SUCCESS_NOTIFICATION,
    WARNING_NOTIFICATION,
    WORKSPACE_SELECTED
} from '../actions'

const TIMEOUT = 5000

const content = (action) => {

    switch (action.type) {

        case CONTENT_RECEIVE:
            return {
                isFetching: false,
                content: action.content,
                response: action.response,
                success: action.success,
                error: action.error,
                lastUpdated: new Date()
            }

        case CONTENT_REQUEST:
            return {
                isFetching: true,
                success: false,
                error: null,
                content: {}
            }

        default:
            return {
                isFetching: false,
                success: false,
                error: null,
                content: {}
            }

    }

}

const contentByKey = (state = {}, action) => {

    switch (action.type) {

        case CONTENT_RECEIVE:
        case CONTENT_REQUEST:
            return {
                ...state,
                [action.key]: content(action)
            }

        default:
            return state

    }

}

const appBusyReducer = (state = {}, action) => {

    let isBusy
    let causes

    switch (action.type) {

        case APP_BUSY_DECREMENT:
            causes = state.causes || []
            causes = causes.filter((why) => {
                return why !== action.why
            })
            isBusy = 0 < causes.length
            return Object.assign({}, state, {
                causes,
                isBusy
            })

        case APP_BUSY_INCREMENT:
            causes = state.causes || []
            if (!causes.includes(action.why)) {
                causes.push(action.why)
            }
            isBusy = true
            return Object.assign({}, state, {
                causes,
                isBusy
            })

        default:
            return state

    }

}

const store = new StoreService()
const deepLinkKey = StringTableService.getString(StringTableService.keys.deepLinkKey)
const builtinReducer = (state = {}, action) => {

    let route

    switch (action.type) {
        case '@@router/LOCATION_CHANGE':
            route = action.payload
                ? [action.payload.pathname, action.payload.search].join('')
                : '/'
            if (!UtilService.skipDeepLinkingRoute(route)) {
                store.set(deepLinkKey, route)
                    .catch((error) => {
                        UtilService.safeLog('Unable to store deep link')
                        UtilService.safeLog(error.stack)
                    })
            }
            return state

        default:
            return state
    }

}

const currentClientReducer = (state = {}, action) => {

    let client

    switch (action.type) {

        case CURRENT_CLIENT_RESET:
            client = null

            return Object.assign({}, state, {
                client
            })

        case CURRENT_CLIENT_UPDATE:
            client = action.client

            return Object.assign({}, state, {
                client
            })

        default:
            return state
    }
}

const authReducer = (state = {}, action) => {

    switch (action.type) {

        case AUTH_FAILURE:
            return Object.assign({}, state, {
                authenticated: false,
                authenticating: false,
                startupCheckDone: true,
                error: action.error
            })

        case AUTH_LOGOUT:
            return Object.assign({}, state, {
                authenticated: false,
                authenticating: false
            })

        case AUTH_REQUEST:
            return Object.assign({}, state, {
                authenticated: false,
                authenticating: true
            })

        case AUTH_SUCCESS:
            return Object.assign({}, state, {
                authenticated: true,
                authenticating: false,
                startupCheckDone: true,
                data: action.data
            })

        default:
            return state

    }

}

const drawerReducer = (state = {}, action) => {

    switch (action.type) {

        case DRAWER_OPEN:
            return Object.assign({}, state, {
                open: true
            })

        case DRAWER_CLOSE:
            return Object.assign({}, state, {
                open: false
            })

        default:
            return state

    }

}

const pageModeReducer = (state = 'primary', action) => {

    switch (action.type) {

        case PAGE_MODE_CHANGE:
            return action.value

        default:
            return state

    }

}

const dragDropReducer = (state = [], action) => {

    switch (action.type) {

        case DRAG_DROP_FILES:
            return state.concat(action.value)

        case DRAG_DROP_RESET:
            return []

        default:
            return state
    }
}

const workspaceKey = StringTableService.getString(StringTableService.keys.workspaceKey)
const workspaceReducer = (state = {}, action) => {

    switch (action.type) {

        case CONTENT_RECEIVE: {
            if (!state.orn) {
                const meCallKey = new OspreyApi().me.info.getKey()

                if (action.key === meCallKey && action.content) {
                    return Object.assign({}, state, action.content.workspace)
                }
            }

            store.set(workspaceKey, JSON.stringify(state))

            return state
        }

        case WORKSPACE_SELECTED: {
            const newState = Object.assign({}, state, action.payload)
            store.set(workspaceKey, JSON.stringify(newState))
            return newState
        }

        default:
            return state
    }

}

const messageCreateReducer = (state = defaultMessageObject, action) => {
    switch (action.type) {
        case MESSAGE_CREATE:
            return Object.assign({}, state, action.value)
        default:
            return state
    }
}

const offerCreateReducer = (state = defaultOffer, action) => {
    switch (action.type) {
        case OFFER_CREATE:
            return Object.assign({}, state, action.value)
        default:
            return state
    }
}

const siloGetSettingsReducer = (state = {}, action) => {
    switch (action.type) {
        case SILO_SETTINGS:
            return Object.assign({}, state, action.value)
        default:
            return state
    }
}

const authorizerReducer = (state = null, action) => {
    switch (action.type) {
        case CONTENT_RECEIVE: {
            const meCallKey = new OspreyApi().me.info.getKey()

            if (action.key === meCallKey && action.content) {
                return new AccessPolicyService(
                    action.content.policy
                )
            }

            return state
        }

        default: {
            return state
        }
    }
}

const responseNotificationReducer = (state = [], action) => {
    const { error, info, notificationType, success, warning } = action
    const notification = Object.assign({}, (error || warning || info || success))

    if (notificationType && error) {
        Object.assign(notification, {
            key: error.raw.response.config.url,
            show: true,
            timeout: new Date().getTime() + TIMEOUT
        })
    }

    switch (notificationType) {
        case ERROR_NOTIFICATION: {
            Object.assign(notification, { type: NOTIFICATION_TYPE.danger })
            return [].concat(state, notification)
        }

        case SUCCESS_NOTIFICATION: {
            Object.assign(notification, { type: NOTIFICATION_TYPE.success })
            return [].concat(state, notification)
        }

        case INFO_NOTIFICATION: {
            Object.assign(notification, { type: NOTIFICATION_TYPE.info })
            return [].concat(state, notification)
        }

        case WARNING_NOTIFICATION: {
            Object.assign(notification, { type: NOTIFICATION_TYPE.warning })
            return [].concat(state, notification)
        }

        case DISMISS_NOTIFICATION: {
            const notifications = Object.assign([], state)
            notifications.pop()
            return notifications.filter(n => n.timeout > new Date().getTime())
        }

        default: {
            return state
        }
    }

}

const getRootReducer = (additionalReducers) => {

    const reducers = {
        _builtin: builtinReducer,
        appBusy: appBusyReducer,
        auth: authReducer,
        authorizer: authorizerReducer,
        viewPort: createResponsiveStateReducer({
            minimal: 479,
            extraSmall: 767,
            small: 1023,
            medium: 1279
        },
            {
                infinity: 'large'
            }),
        contentByKey,
        currentClient: currentClientReducer,
        drawer: drawerReducer,
        pageMode: pageModeReducer,
        dragDropFiles: dragDropReducer,
        workspace: workspaceReducer,
        router: routerReducer,
        messageObject: messageCreateReducer,
        offerObject: offerCreateReducer,
        siloSettingsDict: siloGetSettingsReducer,
        responseNotification: responseNotificationReducer
    }

    Object.assign(reducers, additionalReducers)

    return combineReducers(reducers)

}

export default getRootReducer



// WEBPACK FOOTER //
// src/reducers/index.js